import SuperFetch from 'library/helpers/superFetch';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { rolesConfig } from 'config/rolesConfig';
import { roleActions } from './slice';
import { organisationActions } from 'reduxStore/organisations/slice';

// Existing getRoles saga
export function* getRoles() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		const roles = yield call(() => SuperFetch.get('/roles'));

		if (roles.statusCode === 200) {
			yield put(roleActions.loadRolesSuccess({ orgID, data: roles.data }));
		} else {
			throw Error(roles.error.message || roles.error || 'Failed to load roles.');
		}
	} catch (error) {
		yield put(roleActions.loadRolesError(error));
	}
}

// 🚀 Assign Role Saga
export function* assignRole({ payload }) {
	try {
		// Developer (201) has a dedicated endpoint: grantRole's schema caps permissionID
		// at 200, and only a Developer actor passes grantRoleDeveloper's authorization.
		const endpoint =
			payload.permissionID === rolesConfig.DEVELOPER ? '/roles/grantRoleDeveloper' : '/roles/grantRole';
		const response = yield call(() => SuperFetch.post(endpoint, payload));

		if (response.statusCode === 200) {
			yield put(roleActions.assignRoleSuccess());
			Notification('success', 'Role has been assigned successfully.');
		} else {
			throw Error(response.error?.message || response.error || 'Failed to assign role.');
		}
	} catch (error) {
		yield put(roleActions.assignRoleError(error.message));
		Notification('error', error.message || 'Failed to assign role.');
	}
}

// 🚀 Assign Role Success Saga
export function* assignRoleSuccess() {
	yield put(roleActions.loadRoles());
	yield put(organisationActions.loadOrganisations());
}

// 🚀 Revoke Role Saga — revokes one or many role assignments via the bulk endpoint.
// The backend authorises each roleID independently (a site admin may be able to revoke
// some but not others), so the response reports per-role results with three statuses
// plus a { revoked, denied, failed } summary.
export function* revokeRole({ payload }) {
	try {
		const { roleIDs } = payload;

		// Bulk endpoint accepts at most 50 IDs per request
		const chunks = [];
		for (let i = 0; i < roleIDs.length; i += 50) chunks.push(roleIDs.slice(i, i + 50));

		const responses = yield all(
			chunks.map((chunk) => call(() => SuperFetch.post('/roles/revokeRole/bulk', { roleIDs: chunk })))
		);

		let revoked = 0;
		let denied = 0;
		let failed = 0;
		responses.forEach((res) => {
			if (![200, 204].includes(res.statusCode)) {
				throw Error(res.error?.message || res.error || 'Failed to revoke role(s).');
			}
			const summary = res.data?.summary || res.summary || {};
			revoked += summary.revoked || 0;
			denied += summary.denied || 0;
			failed += summary.failed || 0;
		});

		const skipped = [denied && `${denied} denied`, failed && `${failed} failed`].filter(Boolean).join(', ');

		if (revoked === 0 && skipped) {
			throw Error(`No roles were revoked (${skipped}).`);
		}

		yield put(roleActions.revokeRoleSuccess());

		if (skipped) {
			Notification('warning', 'Some roles were not revoked', `${revoked} revoked, ${skipped}.`);
		} else {
			Notification(
				'success',
				revoked > 1 ? 'Roles revoked' : 'Role revoked',
				revoked > 1 ? `${revoked} role assignments have been revoked.` : 'Role has been revoked successfully.'
			);
		}
	} catch (error) {
		yield put(roleActions.revokeRoleError(error.message));
		Notification('error', error.message || 'Failed to revoke role.');
	}
}

// 🚀 Revoke Role Success Saga
export function* revokeRoleSuccess() {
	yield put(roleActions.loadRoles());
	yield put(organisationActions.loadOrganisations());
}

// 🚀 Root Saga
export default function* rolesSagas() {
	yield takeLatest(roleActions.loadRoles, getRoles);
	yield takeLatest(roleActions.assignRole, assignRole);
	yield takeLatest(roleActions.assignRoleSuccess, assignRoleSuccess); // Reload roles after assignment
	yield takeLatest(roleActions.revokeRole, revokeRole);
	yield takeLatest(roleActions.revokeRoleSuccess, revokeRoleSuccess); // Reload roles after revocation
}
